import React, { useState } from 'react';
import { API_BASE } from '../shared/constants';

type Locale = 'hr' | 'en';

const GOLD = 'var(--ad-acc)';

type Feedback = { kind: 'ok' | 'err'; text: string } | null;
type Candidate = { id: number; name: string };
type Prompt =
    | { kind: 'replace'; checkinId: number; candidates: Candidate[] }
    | { kind: 'overtime'; checkinId: number }
    | null;

const KEYS: string[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'back'];

const timeOf = (iso: string): string => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

type CheckinResp = {
    ok?: boolean; employee_first_name?: string; action?: 'in' | 'out'; at?: string;
    needs_replacement?: boolean; candidates?: Candidate[]; overtime_prompt?: boolean; checkin_id?: number; error?: string;
};

/**
 * Standalone kiosk PIN pad. NEMA app chrome — puni ekran, veliki keypad.
 * Dijeljeni uređaj: prima PIN bilo kojeg djelatnika neovisno o prijavljenom useru.
 * POST /api/kiosk/checkin (javni, rate-limited). ?branch=ID veže kiosk na poslovnicu.
 */
export function KioskScreen({ locale, embedded = false }: { locale: Locale; embedded?: boolean }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const branchId = typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('branch')
        : null;

    const [pin, setPin] = useState('');
    const [busy, setBusy] = useState(false);
    const [feedback, setFeedback] = useState<Feedback>(null);
    const [prompt, setPrompt] = useState<Prompt>(null);

    const press = (k: string) => {
        if (busy) return;
        setFeedback(null);
        if (k === 'clear') { setPin(''); return; }
        if (k === 'back') { setPin((p) => p.slice(0, -1)); return; }
        if (pin.length >= 6) return;
        setPin((p) => p + k);
    };

    const post = async (path: string, body: Record<string, unknown>) => {
        const res = await fetch(`${API_BASE}${path}`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        return (await res.json().catch(() => ({}))) as CheckinResp & { ok?: boolean };
    };

    const submit = async () => {
        if (busy || pin.length < 4) return;
        setBusy(true);
        setFeedback(null);
        try {
            const data = await post('/kiosk/checkin', { pin, department_id: branchId ? Number(branchId) : undefined });
            if (!data.ok) {
                setFeedback({ kind: 'err', text: t('Neispravan ili istekao PIN.', 'Invalid or expired PIN.') });
                setPin('');
                return;
            }
            const name = data.employee_first_name ?? '';
            const when = data.at ? timeOf(data.at) : '';
            setPin('');

            if (data.action === 'out') {
                setFeedback({ kind: 'ok', text: t(`Evidentiran je odlazak — ${name}, ${when}.`, `Check-out recorded — ${name}, ${when}.`) });
                if (data.overtime_prompt && data.checkin_id) {
                    setPrompt({ kind: 'overtime', checkinId: data.checkin_id });
                }
                return;
            }
            // prijava
            setFeedback({ kind: 'ok', text: t(`Evidentiran je dolazak — ${name}, ${when}.`, `Check-in recorded — ${name}, ${when}.`) });
            if (data.needs_replacement && data.checkin_id) {
                setPrompt({ kind: 'replace', checkinId: data.checkin_id, candidates: data.candidates ?? [] });
            }
        } catch {
            setFeedback({ kind: 'err', text: t('Greška veze. Pokušaj ponovno.', 'Connection error. Try again.') });
        } finally {
            setBusy(false);
        }
    };

    const chooseReplacement = async (c: Candidate) => {
        if (!prompt || prompt.kind !== 'replace') return;
        setBusy(true);
        try {
            await post('/kiosk/checkin/replace', { checkin_id: prompt.checkinId, replaced_employee_id: c.id });
            setFeedback({ kind: 'ok', text: t(`Evidentirano: mijenjaš ${c.name}.`, `Recorded: replacing ${c.name}.`) });
        } catch { /* ostavi dolazak evidentiran */ }
        finally { setPrompt(null); setBusy(false); }
    };

    const answerOvertime = async (yes: boolean) => {
        if (!prompt || prompt.kind !== 'overtime') return;
        setBusy(true);
        try {
            await post('/kiosk/checkin/overtime', { checkin_id: prompt.checkinId, is_overtime: yes });
            if (yes) setFeedback({ kind: 'ok', text: t('Označeno kao prekovremeni rad.', 'Marked as overtime.') });
        } catch { /* nije kritično */ }
        finally { setPrompt(null); setBusy(false); }
    };

    const shellStyle: React.CSSProperties = embedded
        ? {
            position: 'relative', width: '100%', minHeight: 540, borderRadius: 18,
            background: 'linear-gradient(160deg,#2b2410,#0f0c05)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'system-ui, sans-serif', color: '#fff', padding: 24, boxSizing: 'border-box',
        }
        : {
            position: 'fixed', inset: 0, background: 'linear-gradient(160deg,#2b2410,#0f0c05)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'system-ui, sans-serif', color: '#fff', padding: 24, boxSizing: 'border-box',
        };

    // ── Prompt: koga mijenjaš ──
    if (prompt?.kind === 'replace') {
        return (
            <div style={shellStyle}>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: GOLD, marginBottom: 6 }}>{t('Nisi u rasporedu', 'Not on schedule')}</div>
                <div style={{ color: '#d8c79a', marginBottom: 18 }}>{t('Koga mijenjaš?', 'Who are you replacing?')}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: 320, maxHeight: '50vh', overflowY: 'auto' }}>
                    {prompt.candidates.length === 0 ? (
                        <div style={{ color: '#9a8a55', textAlign: 'center' }}>{t('Nema kandidata na ovoj poslovnici.', 'No candidates at this branch.')}</div>
                    ) : prompt.candidates.map((c) => (
                        <button key={c.id} type="button" disabled={busy} onClick={() => chooseReplacement(c)}
                            style={{ padding: '16px', borderRadius: 14, fontSize: '1.1rem', fontWeight: 600, border: '1px solid rgba(var(--ad-acc-rgb),0.5)', background: 'rgba(255,255,255,0.08)', color: '#fff', cursor: 'pointer' }}>
                            {t('Mijenjam:', 'Replacing:')} {c.name}
                        </button>
                    ))}
                    <button type="button" disabled={busy} onClick={() => setPrompt(null)}
                        style={{ padding: '14px', borderRadius: 14, fontSize: '1rem', border: 'none', background: 'rgba(var(--ad-acc-rgb),0.18)', color: GOLD, cursor: 'pointer', marginTop: 8 }}>
                        {t('Nikoga / preskoči', 'No one / skip')}
                    </button>
                </div>
            </div>
        );
    }

    // ── Prompt: prekovremeni ──
    if (prompt?.kind === 'overtime') {
        return (
            <div style={shellStyle}>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: GOLD, marginBottom: 6 }}>{t('Više od 8 sati', 'Over 8 hours')}</div>
                <div style={{ color: '#d8c79a', marginBottom: 22, textAlign: 'center' }}>{t('Jesu li ovo prekovremeni sati?', 'Are these overtime hours?')}</div>
                <div style={{ display: 'flex', gap: 16 }}>
                    <button type="button" disabled={busy} onClick={() => answerOvertime(true)}
                        style={{ width: 140, height: 64, borderRadius: 16, fontSize: '1.2rem', fontWeight: 700, border: 'none', background: GOLD, color: '#2b2410', cursor: 'pointer' }}>{t('DA', 'YES')}</button>
                    <button type="button" disabled={busy} onClick={() => answerOvertime(false)}
                        style={{ width: 140, height: 64, borderRadius: 16, fontSize: '1.2rem', fontWeight: 700, border: '1px solid rgba(var(--ad-acc-rgb),0.5)', background: 'rgba(255,255,255,0.08)', color: '#fff', cursor: 'pointer' }}>{t('NE', 'NO')}</button>
                </div>
            </div>
        );
    }

    // ── PIN pad ──
    return (
        <div style={shellStyle}>
            <div style={{ marginBottom: 18, textAlign: 'center' }}>
                <div style={{ fontSize: '1.6rem', fontWeight: 700, color: GOLD, letterSpacing: '0.04em' }}>
                    {t('Evidencija radnog vremena', 'Time clock')}
                </div>
                <div style={{ fontSize: '0.95rem', color: '#d8c79a', marginTop: 4 }}>
                    {t('Unesi svoj PIN', 'Enter your PIN')}
                </div>
            </div>

            <div style={{ fontSize: '2.4rem', letterSpacing: '0.5rem', fontFamily: 'monospace', minHeight: '3rem', color: '#fff', marginBottom: 6, minWidth: 220, textAlign: 'center' }}>
                {pin ? '•'.repeat(pin.length) : <span style={{ color: '#6b5a30' }}>-----</span>}
            </div>

            <div style={{ minHeight: 28, marginBottom: 10, textAlign: 'center' }}>
                {feedback ? (
                    <div style={{ fontSize: '1.05rem', fontWeight: 600, color: feedback.kind === 'ok' ? '#7fd47f' : '#ff8a8a' }}>{feedback.text}</div>
                ) : null}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 88px)', gap: 14 }}>
                {KEYS.map((k) => {
                    const isAction = k === 'clear' || k === 'back';
                    const label = k === 'clear' ? 'C' : k === 'back' ? '⌫' : k;
                    return (
                        <button key={k} type="button" onClick={() => press(k)} disabled={busy}
                            style={{ width: 88, height: 88, borderRadius: 18, fontSize: '1.8rem', fontWeight: 600, cursor: busy ? 'default' : 'pointer', border: '1px solid rgba(var(--ad-acc-rgb),0.5)', background: isAction ? 'rgba(var(--ad-acc-rgb),0.18)' : 'rgba(255,255,255,0.08)', color: isAction ? GOLD : '#fff' }}>{label}</button>
                    );
                })}
            </div>

            <button type="button" onClick={submit} disabled={busy || pin.length < 4}
                style={{ marginTop: 22, width: 88 * 3 + 28, height: 64, borderRadius: 18, fontSize: '1.2rem', fontWeight: 700, border: 'none', cursor: busy || pin.length < 4 ? 'default' : 'pointer', background: busy || pin.length < 4 ? '#5a4a22' : GOLD, color: busy || pin.length < 4 ? '#9a8a55' : '#2b2410' }}>
                {busy ? t('Šaljem…', 'Sending…') : t('Prijavi / Odjavi', 'Check in / out')}
            </button>
        </div>
    );
}
